import { computed, type ComputedRef, type Ref } from 'vue'

import { GemsApi_StoneShape, type GemsFindStonesQuery } from '@/graphql/api'
import {
    type GemsApiStoneShapeWithInfoWithGemsApiShapeSystemCode,
    useAllowedShapesWithInfo,
} from './sku/shape'
import { findShapeOrder } from '@/domain/stone/search/useOrderedShapeFragments'
import type { RingBuilderCentreStoneShapeConstraint } from '@/domain/product/ring/ring-builder.ts'
import { includes } from '@/helpers'
import {
    useRingBuilder,
    useRingBuilderRingShape,
} from '@/plugins/ring-builder.ts'

export type RingBuilderCentreStoneShapeConstraintWithGemsApiShapeSystemCode =
    Omit<RingBuilderCentreStoneShapeConstraint, 'systemCode'> & {
        systemCode: GemsApi_StoneShape
    }

type RingShapeConstraintForGemsAPI = {
    ringShape: ComputedRef<
        | RingBuilderCentreStoneShapeConstraintWithGemsApiShapeSystemCode
        | undefined
    >
    shapes: ComputedRef<
        GemsApiStoneShapeWithInfoWithGemsApiShapeSystemCode[] | undefined
    >
}

export function useRingShapeConstraintForGemsAPI(
    bounds: Readonly<Ref<GemsFindStonesQuery['GemsApi_getBounds']>>
): RingShapeConstraintForGemsAPI {
    const { mountFragment } = useRingBuilder()
    const ringBuilderOtherShapes = computed(() =>
        mountFragment.value?.ring?.variationsByCentreStoneShape.map(
            (variation) => variation.centreStoneConstraints.shape.systemCode
        )
    )

    const ringShapeWithMonolithCode = useRingBuilderRingShape()

    const ringShape = computed(() => {
        const monolithShape = ringShapeWithMonolithCode.value

        if (!monolithShape) {
            return undefined
        }

        const allShapes = Object.values(GemsApi_StoneShape)
        const shapeSystemCode = monolithShape.systemCode

        if (includes(allShapes, shapeSystemCode)) {
            return {
                ...monolithShape,
                systemCode: shapeSystemCode,
            }
        }

        return undefined
    })
    const shapeBounds = useAllowedShapesWithInfo(() => bounds.value?.shapes)
    const shapes = computed(() => {
        const shapes = ringBuilderOtherShapes.value
            ? shapeBounds.value?.filter((item) =>
                  ringBuilderOtherShapes.value?.includes(
                      item.monolithSystemCode
                  )
              )
            : shapeBounds.value

        // https://taylorandhart.atlassian.net/browse/TH-8950
        // https://docs.google.com/document/d/16hW7z3SRulagna6bb4O-SCIxJ4fnBeNVBhTCQKpCF0Y/edit
        // Some day this might become a Monolith feature
        return shapes?.sort(
            (
                a: GemsApiStoneShapeWithInfoWithGemsApiShapeSystemCode,
                b: GemsApiStoneShapeWithInfoWithGemsApiShapeSystemCode
            ) => {
                const orderA = findShapeOrder(a.systemCode)
                const orderB = findShapeOrder(b.systemCode)

                return orderA - orderB
            }
        )
    })

    return { shapes, ringShape }
}
